import { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps, useHistory, useLocation, withRouter } from 'react-router-dom';
import { AxiosError } from 'axios';
import useOverlay from 'src/utils/overlay-provider';
import useArticleDetails from 'src/utils/contexts/articleContext';
import TemplateRenderer, { TEMPLATE } from 'src/templates/template-renderer/template-renderer';
import usePageBodyDetails from 'src/utils/contexts/pageBodyContext';
import ResourcesSingleton from 'src/utils/resource';
import { Maybe } from 'src/types/maybe';
import authService from 'src/components/authorization/AuthorizeService';
import { FMHBackToTopButton } from 'src/atoms/fmh-back-to-top-button';
import { Skeleton } from 'src/components/loading-skeleton';
import { SkeletonType } from 'src/components/loading-skeleton/dto';
import LanguageFunction from '@/utils/languageCode';
import { PageBannerType, PageType } from '@/types/page-type';
import PageService from '@/services/page-service';
import { Logger } from '@/utils/logger';
import { Main } from '@/components/main';
import Footer from '@/organisms/ff-footer/ff-footer';

interface MatchParams {
    name: string;
}

interface DynamicProps extends RouteComponentProps<MatchParams> {
    setFooterVisibilityFunc: (b: boolean) => void;
    setHeaderVisibilityFunc: (b: boolean) => void;
    headerFooterLoading: boolean;
    showFooter: boolean;
    headless?: Maybe<boolean>;
}

const HIDE_HEADER_FOOTER_TEMPLATES = [TEMPLATE.LOG_IN_HOLDING_SCREEN, TEMPLATE.REGISTRATION_FORM_TEMPLATE];

const Dynamic = (props: DynamicProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<PageType>();
    const history = useHistory();
    const location = useLocation();
    const overlay = useOverlay();
    const { setRelativeUrl, setArticleName } = useArticleDetails();
    const { setLanguageDropdownData } = usePageBodyDetails();
    const [liveGameBanner, setLiveGameBanner] = useState<PageBannerType>();

    const addPageBanner = useCallback(
        (banner: PageBannerType, overlay): boolean => {
            if (!banner) return false;

            const isLiveGameBanner = !!banner.tournamentFdcpIds?.length;

            if (isLiveGameBanner) {
                setLiveGameBanner?.(banner);
                return true;
            } else {
                overlay.addBanner(banner.message, banner.title, banner.link, null, banner.isClosable, banner);
            }
            return false;
        },
        [setLiveGameBanner],
    );

    const setCorrectPageState = useCallback(
        (pageResponse: PageType | null) => {
            if (pageResponse) {
                setPage(pageResponse);
                document.title = pageResponse.title ?? 'FIFA Media Hub';
                if (!pageResponse?.headless) {
                    window.history.replaceState(null, '', `/${pageResponse.relativeUrl}${location.search}`);
                }
                overlay.clear();
                let isLiveGameBannerPresent = false;

                pageResponse.banners?.forEach((banner) => {
                    isLiveGameBannerPresent = addPageBanner(banner, overlay) || isLiveGameBannerPresent;
                });

                if (!isLiveGameBannerPresent) {
                    setLiveGameBanner?.(undefined);
                }

                if (setLanguageDropdownData) {
                    setLanguageDropdownData({
                        languageDropdown: pageResponse.availableLanguages ?? [],
                        currentLocale: pageResponse.language,
                    });
                }

                LanguageFunction.setLocalStorageLocale(pageResponse.language);
            } else {
                history.push('/en/404');
            }
        },
        [addPageBanner, history, location.search, overlay, setLanguageDropdownData, setLiveGameBanner],
    );

    useEffect(() => {
        const route = props.headless
            ? props.match.url.replace('/headless', '').replace('/mobile', '')
            : props.match.url;

        const initializePage = (route: string) => {
            if (route === '/') route = '/en/home';

            PageService.getPageByRelativeUrl(LanguageFunction.getCorrectApiPageUrl(route), route)
                .then((page: PageType) => {
                    page.headless = props.headless;
                    setCorrectPageState(page);
                    const template = page.template as TEMPLATE;
                    if (![TEMPLATE.ARTICLE, TEMPLATE.VIDEO_PAGE].includes(template)) {
                        if (setArticleName) setArticleName(undefined);
                    }
                    if (setRelativeUrl) setRelativeUrl(page.relativeUrl);
                    const urlParams = new URLSearchParams(props.location.search);
                    const preview = urlParams.get('preview') ?? 'false';
                    if (preview === 'true') {
                        overlay.addBanner('You are currently using preview mode', null, null, null, true);
                    }
                    return ResourcesSingleton.getResourceSetData();
                })
                .catch((e: AxiosError) => {
                    if (e.response?.status === 404) {
                        if (LanguageFunction.getLocalStorageLocale() !== 'en') {
                            PageService.getPageByRelativeUrl(
                                LanguageFunction.getCorrectApiPageUrl(route, LanguageFunction.defaultLanguage.id),
                                route,
                            )
                                .then((page: PageType) => {
                                    LanguageFunction.setLocalStorageLocale(LanguageFunction.defaultLanguage.id);
                                    if (setRelativeUrl) setRelativeUrl(page.relativeUrl);
                                    history.replace(`/${page.relativeUrl}`);
                                })
                                .catch((e: AxiosError) => {
                                    if (e.response?.status === 404) {
                                        setCorrectPageState(null);
                                    }
                                });
                        } else {
                            setCorrectPageState(null);
                        }
                    }
                })
                .finally(() => setLoading(false));
        };

        const initializePageWithToken = async () => {
            const isTokenExpired = await authService.isTokenExpired();

            if (isTokenExpired) {
                await authService.tryRefreshToken();
            }
            initializePage(route);
        };

        initializePageWithToken();

        Logger.info(`[ROUTE] ${route}`);
    }, [
        props.match.url,
        history,
        props.headless,
        props.location.search,
        setCorrectPageState,
        setRelativeUrl,
        setArticleName,
        overlay,
        location.pathname,
        location.search,
    ]);

    useEffect(() => {
        const template = page?.template || '';
        const shouldShowHeaderFooter = !HIDE_HEADER_FOOTER_TEMPLATES.includes(template as TEMPLATE) && !props.headless;

        props.setFooterVisibilityFunc(shouldShowHeaderFooter);
        props.setHeaderVisibilityFunc(shouldShowHeaderFooter);
    }, [page?.template, props]);

    return (
        <>
            {loading && !props.headless && <Skeleton variant={SkeletonType.FullPageSpinner} />}
            {!loading && page && (
                <Main title={page.meta?.title} description={page.meta?.title}>
                    <TemplateRenderer page={page} liveGamesBanner={liveGameBanner} />
                    {!props.headerFooterLoading ? (
                        props.showFooter && <Footer pageRelativeUrl={page!.relativeUrl} />
                    ) : (
                        <Skeleton variant={SkeletonType.Footer} />
                    )}
                    {!props.headless && <FMHBackToTopButton />}
                </Main>
            )}
        </>
    );
};

export default withRouter(Dynamic);
